import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { getCounterReport } from '../../services/reportService';
import locationService from '../../services/locationService';
import locationUserService from '../../services/locationUserService';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';
import Layout from '../../components/layout/Layout';

const CounterReport = () => {
  const [loading, setLoading] = useState(true);
  const [fullReportData, setFullReportData] = useState({
    totalBills: 0,
    totalRevenue: 0,
    totalUsers: 0,
    userSummaries: []
  });
  
  // Date state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    // Default to today
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });

  // Filters
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // User Filter
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Check user role on mount
  useEffect(() => {
    const role = localStorage.getItem('userRole');
    setIsAdmin(role === 'ADMIN');
    
    if (role === 'ADMIN') {
      fetchLocations();
      fetchUsers();
    } else {
        // If not admin, we might still want to fetch users for the current location
        // But the current API structure might be tricky. Let's try fetching all users for now or rely on what we have.
        // Actually, for non-admins, they might only see their own location's users.
        fetchUsers();
    }
  }, []);

  // Fetch locations for admin
  const fetchLocations = async () => {
    try {
      const response = await locationService.list({ size: 100 });
      if (response && response.content) {
        setLocations(response.content);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
    }
  };

  // Fetch users for filter
  const fetchUsers = async () => {
    try {
        // Fetch all users to populate the dropdown
        // In a real scenario with thousands of users, we'd want server-side filtering or location-based fetching.
        // For now, assuming manageable number of users.
        const response = await locationUserService.getAllUsers({ size: 1000 });
        if (response && response.content) {
            setUsers(response.content);
        }
    } catch (error) {
        console.error('Error fetching users:', error);
        // Don't block the report if users fail to load
    }
  };

  // Fetch report data
  const fetchReport = async () => {
    try {
      setLoading(true);
      // locationId should be null if not selected or if not admin (handled by backend too, but good to be clean)
      const locId = isAdmin && selectedLocation ? selectedLocation : null;
      
      const data = await getCounterReport(startDate, endDate, locId);
      setFullReportData(data);
    } catch (error) {
      console.error('Error fetching counter report:', error);
      toast.error('Failed to load counter report');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const handleGenerateReport = () => {
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date cannot be earlier than start date');
      return;
    }
    fetchReport();
  };

  // Calculate displayed data based on filters
  const getDisplayedData = () => {
      let filteredSummaries = fullReportData.userSummaries;

      // Filter by selected User from the top dropdown
      if (selectedUser) {
          filteredSummaries = filteredSummaries.filter(summary => summary.userId === selectedUser.id);
      }

      // Re-calculate totals based on filtered data
      const totalBills = filteredSummaries.reduce((sum, item) => sum + item.billCount, 0);
      const totalRevenue = filteredSummaries.reduce((sum, item) => sum + item.totalRevenue, 0); // Assuming totalRevenue is number
      const totalUsers = filteredSummaries.length;

      return {
          totalBills,
          totalRevenue,
          totalUsers,
          userSummaries: filteredSummaries
      };
  };

  const displayedData = getDisplayedData();

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      
      const { userSummaries } = displayedData;

      // Prepare the data for export
      const exportData = userSummaries.map((summary, index) => ({
        'S.No': index + 1,
        'User Name': summary.userName || '-',
        'Location': summary.locationName || '-',
        'Bill Count': summary.billCount || 0,
        'Total Revenue': summary.totalRevenue || 0
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      const wscols = [
        { wch: 8 },  // S.No
        { wch: 30 }, // User Name
        { wch: 30 }, // Location
        { wch: 15 }, // Bill Count
        { wch: 20 }  // Total Revenue
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add report header info - Following the pattern from SalesReport.js
      XLSX.utils.sheet_add_aoa(ws, [
        ['Counter / User-wise Sales Report'],
        ['Generated on: ' + formatDateTime(new Date())],
        ['Period: ' + formatDate(startDate) + ' to ' + formatDate(endDate)],
        [''] // Empty row for spacing
      ], { origin: 'A1' });

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Counter Report');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `counter_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Counter report exported successfully');
    } catch (error) {
      console.error('Error exporting counter report:', error);
      toast.error('Failed to export counter report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 3,
        backgroundColor: 'background.default',
        minHeight: '100vh'
      }}
    >
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} color="inherit" to="/dashboard">
            Dashboard
          </Link>
          <Link component={RouterLink} color="inherit" to="/reports">
            Reports
          </Link>
          <Typography color="text.primary">Counter Reports</Typography>
        </Breadcrumbs>

        {/* Title & Export */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0 }}>
            Counter / User-wise Sales Report
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExportToExcel}
            disabled={isExporting || displayedData.userSummaries.length === 0}
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              {isAdmin && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={selectedLocation}
                      label="Location"
                      onChange={(e) => setSelectedLocation(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All Locations</em>
                      </MenuItem>
                      {locations.map((loc) => (
                        <MenuItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
               <Grid item xs={12} md={3}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedUser}
                  onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Filter by User" fullWidth />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                        <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.location ? option.location.name : 'No Location'}
                            </Typography>
                        </Box>
                    </li>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGenerateReport}
                  size="large"
                  sx={{ height: '56px' }}
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ height: '100%', bgcolor: 'primary.light', color: 'white' }}>
                  <CardContent>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item>
                        <ReceiptIcon sx={{ fontSize: 40 }} />
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" gutterBottom>
                          Total Bills
                        </Typography>
                        <Typography variant="h4">
                          {displayedData.totalBills}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ height: '100%', bgcolor: 'success.light', color: 'white' }}>
                  <CardContent>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item>
                        <AttachMoneyIcon sx={{ fontSize: 40 }} />
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" gutterBottom>
                          Total Revenue
                        </Typography>
                        <Typography variant="h4">
                          {formatCurrency(displayedData.totalRevenue)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ height: '100%', bgcolor: 'info.light', color: 'white' }}>
                  <CardContent>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item>
                        <PeopleIcon sx={{ fontSize: 40 }} />
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" gutterBottom>
                          Active Users
                        </Typography>
                        <Typography variant="h4">
                          {displayedData.totalUsers}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Table */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    User-wise Details
                  </Typography>
                </Box>
                
                <TableContainer component={Paper} elevation={0} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell><strong>User Name</strong></TableCell>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell align="right"><strong>Bill Count</strong></TableCell>
                        <TableCell align="right"><strong>Total Revenue</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedData.userSummaries.length > 0 ? (
                        displayedData.userSummaries.map((summary) => (
                          <TableRow key={summary.userId} hover>
                            <TableCell>{summary.userName}</TableCell>
                            <TableCell>{summary.locationName}</TableCell>
                            <TableCell align="right">{summary.billCount}</TableCell>
                            <TableCell align="right">{formatCurrency(summary.totalRevenue)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Box>
    </Layout>
  );
};

export default CounterReport;
